import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { api, type Course } from "@/api/client";
import Header from "@/components/Layout/Header";

export default function CourseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  // Workspace-first: „Neuer Kurs" = minimaler Inline-Dialog (nur Titel,
  // optional Zielgruppe) → direkt in den leeren Workspace (Konzept §8.3).
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newAudience, setNewAudience] = useState("");
  const [newBusy, setNewBusy] = useState(false);

  useEffect(() => {
    api
      .listCourses()
      .then(setCourses)
      .finally(() => setLoading(false));
  }, []);

  const createCourse = async () => {
    const title = newTitle.trim();
    if (!title || newBusy) return;
    setNewBusy(true);
    try {
      const course = await api.createCourse({
        title,
        audience: newAudience.trim() || undefined,
      });
      navigate(`/courses/${course.id}/workspace`);
    } finally {
      setNewBusy(false);
    }
  };

  const removeCourse = async (e: React.MouseEvent, c: Course) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = confirm(t("list.removeConfirm", { title: c.title }));
    if (!ok) return;
    await api.archiveCourse(c.id);
    setCourses((prev) => prev.filter((x) => x.id !== c.id));
  };

  const statusLabels: Record<string, string> = {
    draft: t("list.status.draft"),
    in_review: t("list.status.in_review"),
    approved: t("list.status.approved"),
    archived: t("list.status.archived"),
  };

  return (
    <>
      <Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-4xl font-black text-ca-ink tracking-tighter uppercase italic">
              {t("list.title")}
            </h1>
            <p className="text-ca-ink-muted mt-2">
              {t("list.subtitle")}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowNew((s) => !s)}
              className="px-6 py-4 bg-ca-accent text-ca-brand font-bold uppercase tracking-wider rounded-md hover:bg-brand-gold-dark transition active:scale-95"
            >
              {t("list.emptyNew")}
            </button>
            <button
              onClick={() => navigate("/new")}
              className="px-6 py-4 bg-ca-card border border-ca-surface-border text-ca-surface-muted font-bold uppercase tracking-wider rounded-md hover:text-ca-surface-ink transition active:scale-95"
            >
              {t("list.wizardLink")}
            </button>
            <button
              onClick={() => navigate("/extract")}
              className="px-6 py-4 bg-ca-card border border-ca-surface-border text-ca-surface-muted font-bold uppercase tracking-wider rounded-md hover:text-ca-surface-ink transition active:scale-95"
            >
              {t("list.fromExtract")}
            </button>
          </div>
        </div>

        {showNew && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void createCourse();
            }}
            className="mb-8 p-6 bg-ca-card rounded-2xl border border-ca-surface-border flex flex-wrap items-end gap-4"
          >
            <div className="flex-1 min-w-56">
              <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
                {t("wizard.step1.titleLabel")}
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                autoFocus
                className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
                required
              />
            </div>
            <div className="flex-1 min-w-56">
              <label className="text-xs font-black uppercase text-ca-surface-muted tracking-widest mb-2 block">
                {t("list.newAudienceLabel")}
              </label>
              <input
                type="text"
                value={newAudience}
                onChange={(e) => setNewAudience(e.target.value)}
                className="w-full bg-white border border-ca-surface-border rounded-md px-3 py-2 text-ca-surface-ink outline-none focus:border-ca-accent"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={!newTitle.trim() || newBusy}
                className="px-6 py-2.5 bg-ca-accent text-ca-brand font-bold uppercase tracking-wider rounded-md hover:bg-brand-gold-dark disabled:opacity-40 transition active:scale-95"
              >
                {t("list.newCreate")}
              </button>
              <button
                type="button"
                onClick={() => setShowNew(false)}
                className="px-4 py-2.5 text-ca-surface-muted font-bold uppercase tracking-wider rounded-md border border-ca-surface-border hover:text-ca-surface-ink transition"
              >
                {t("common.cancel")}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="text-ca-ink-muted">{t("common.loadingEllipsis")}</p>
        ) : courses.length === 0 ? (
          <div className="p-16 bg-ca-card rounded-2xl border border-ca-surface-border text-center">
            <p className="text-ca-surface-muted text-lg mb-8">
              {t("list.empty")}
            </p>
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={() => navigate("/new")}
                className="px-10 py-5 bg-ca-accent text-ca-brand font-bold uppercase tracking-wider rounded-md shadow-md hover:bg-brand-navy-light transition active:scale-95"
              >
                {t("list.guidedNew")}
              </button>
              <button
                onClick={() => navigate("/extract")}
                className="text-ca-surface-accent font-bold uppercase text-xs tracking-wider hover:underline transition"
              >
                {t("list.emptyUploadHint")}
              </button>
              <button
                onClick={() => setShowNew(true)}
                className="text-ca-surface-muted font-bold uppercase text-xs tracking-wider hover:text-ca-surface-ink transition"
              >
                {t("list.emptyCreateHint")}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {courses.map((c) => (
              <Link
                key={c.id}
                to={`/courses/${c.id}/workspace`}
                className="block p-6 bg-ca-card rounded-2xl border border-ca-surface-border hover:border-ca-accent/50 transition"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h3 className="text-xl font-black text-ca-surface-ink truncate">{c.title}</h3>
                    <p className="text-sm text-ca-surface-muted mt-1">
                      {c.audience || "—"} · {c.format || t("list.formatOpen")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-xs font-black uppercase tracking-widest px-3 py-1.5 bg-white rounded-full border border-ca-surface-border text-ca-surface-muted">
                      {statusLabels[c.status] || c.status}
                    </span>
                    <button
                      onClick={(e) => removeCourse(e, c)}
                      title={t("list.removeTitle")}
                      className="text-ca-surface-muted hover:text-red-600 text-lg px-2 transition"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
